import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ShoppingBag, Info } from "lucide-react";

const inr = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const PERIODS = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["month", "This month"], ["custom", "Custom"]];

export function CrossSellCard() {
  const [period, setPeriod] = useState("30d");
  const [range, setRange] = useState({ start: "", end: "" });
  const ready = period !== "custom" || (range.start && range.end);
  const q = useQuery({ queryKey: ["cross-sell", period, range], enabled: !!ready,
    queryFn: async () => (await api.get("/dashboard/cross-sell", { params: { period, ...(period === "custom" ? range : {}) } })).data });
  const d = q.data;
  const max = Math.max(1, ...(d?.trend || []).map((t) => t.revenue));
  return (
    <section className="rounded-2xl border border-pawkt-border bg-white p-5 shadow-card" data-testid="cross-sell-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-base font-bold text-pawkt-text"><ShoppingBag className="h-4 w-4 text-emerald-600" /> Cross-Sell Performance</h3>
        <div className="flex flex-wrap gap-1">
          {PERIODS.map(([v, l]) => <button key={v} onClick={() => setPeriod(v)} data-testid={`cross-sell-period-${v}`}
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${period === v ? "bg-emerald-600 text-white" : "bg-pawkt-elevated text-pawkt-text-secondary"}`}>{l}</button>)}
        </div>
      </div>
      {period === "custom" && <div className="mt-2 flex gap-2">
        <input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="rounded-md border px-2 py-1 text-xs" data-testid="cross-sell-start" />
        <input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="rounded-md border px-2 py-1 text-xs" data-testid="cross-sell-end" />
      </div>}
      {q.isError && <p className="mt-3 text-xs text-rose-600">Could not load cross-sell data.</p>}
      {d && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi primary label="Cross-sell revenue (excl. GST)" value={inr(d.revenue_ex_gst)} testid="cs-revenue-ex" />
            <Kpi label="Incl. GST" value={inr(d.revenue_incl_gst)} sub={`GST ${inr(d.gst)}`} testid="cs-revenue-incl" />
            <Kpi label="Items sold" value={d.items_sold} testid="cs-items" />
            <Kpi label="Invoices with cross-sell" value={`${d.invoices_with_cross_sell} / ${d.eligible_invoices} eligible`} testid="cs-invoices" />
            <Kpi label={<span className="inline-flex items-center gap-1">Avg per eligible invoice
              <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="h-3 w-3 cursor-help" data-testid="cs-avg-info" /></TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">{d.avg_definition}</TooltipContent></Tooltip></TooltipProvider></span>}
              value={inr(d.avg_per_eligible_invoice)} testid="cs-avg" />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <List title="By department" rows={d.by_department.map((r) => [r.department, inr(r.revenue)])} testid="cs-by-dept" />
            <List title="Top by revenue" rows={d.top_by_revenue.map((r) => [r.name, inr(r.revenue)])} testid="cs-top-rev" />
            <List title="Top by quantity" rows={d.top_by_qty.map((r) => [r.name, r.qty])} testid="cs-top-qty" />
          </div>
          <div className="mt-4" data-testid="cs-trend">
            <p className="text-[11px] font-bold uppercase tracking-wide text-pawkt-text-secondary">{d.trend_granularity === "month" ? "Monthly" : "Daily"} trend</p>
            {!d.trend.length ? <p className="text-xs text-pawkt-text-disabled">No cross-sells in this period.</p> : (
              <div className="mt-2 flex h-20 items-end gap-1">
                {d.trend.map((t) => <div key={t.bucket} title={`${t.bucket}: ${inr(t.revenue)}`} className="flex-1 rounded-t bg-emerald-400" style={{ height: `${(t.revenue / max) * 100}%` }} />)}
              </div>)}
          </div>
        </>)}
    </section>
  );
}

const Kpi = ({ label, value, sub, primary, testid }) => (
  <div className={`rounded-xl p-3 ${primary ? "bg-emerald-50 ring-1 ring-emerald-200" : "bg-pawkt-elevated"}`} data-testid={testid}>
    <p className="text-[11px] font-semibold text-pawkt-text-secondary">{label}</p>
    <p className={`font-display font-extrabold text-pawkt-text ${primary ? "text-xl" : "text-base"}`}>{value}</p>
    {sub && <p className="text-[11px] text-pawkt-text-disabled">{sub}</p>}
  </div>
);
const List = ({ title, rows, testid }) => (
  <div data-testid={testid}>
    <p className="text-[11px] font-bold uppercase tracking-wide text-pawkt-text-secondary">{title}</p>
    {!rows.length ? <p className="text-xs text-pawkt-text-disabled">—</p> : rows.map(([a, b], i) => (
      <div key={i} className="flex justify-between border-b border-pawkt-border py-1 text-xs"><span className="truncate pr-2 capitalize">{a}</span><span className="font-semibold">{b}</span></div>))}
  </div>
);
